import React, { useState } from 'react';
import { Mail, Building, Phone, User, Package, CheckCircle, AlertCircle } from 'lucide-react'; // Import Lucide icons

function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    products: '',
    consent: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Define the API URL using process.env for security
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setMessage(''); // Clear previous messages

    try {
      const response = await fetch(`${API_URL}/contactus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setMessage('Your message has been sent successfully!');
        setMessageType('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          products: '',
          consent: false,
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting form data:', error);
      setMessage(error.message || 'Failed to send message. Please try again.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen bg-white text-[#1f2937] font-inter overflow-hidden flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Animated Background Elements - Adapted for white theme */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Geometric Shapes (Blue/Cyan theme) */}
        <div className="floating-shape shape-1-light"></div>
        <div className="floating-shape shape-2-light"></div>
        <div className="floating-shape shape-3-light"></div>
        <div className="floating-shape shape-4-light"></div>
        
        {/* Animated Grid Background (Light theme) */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-grid-pattern-light animate-grid-move"></div>
        </div>

        {/* New: Digital Rain Effect (Blue/Cyan theme) */}
        <div className="absolute inset-0 digital-rain-container">
          {Array.from({ length: 50 }).map((_, i) => (
            <div 
              key={i} 
              className="digital-rain-line-light" // Changed class for light theme
              style={{ 
                left: `${Math.random() * 100}vw`, 
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            ></div>
          ))}
        </div>

        {/* Scanning Lines (Blue/Cyan theme) */}
        <div className="absolute inset-0">
          <div className="scan-line-1-light"></div> {/* Changed class for light theme */}
          <div className="scan-line-2-light"></div> {/* Changed class for light theme */}
        </div>
      </div>

      {/* Main Content Container with Subtle Glow */}
      <div className="relative z-10 max-w-2xl w-full bg-white rounded-3xl border border-[#e2e8f0] p-8 lg:p-10 shadow-2xl animate-fade-in animation-delay-400">

        {/* Header Section */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] rounded-full flex items-center justify-center animate-pulse-glow-blue">
                <Mail className="w-10 h-10 text-white animate-rotate-slow" /> {/* Lucide Mail icon */}
              </div>
            </div>
          </div>

          <h1 className="text-4xl lg:text-6xl font-black text-[#1f2937] mb-6 leading-tight animate-fade-in-up">
            CONNECT WITH <span className="bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] bg-clip-text text-transparent animate-gradient-text">US</span>
          </h1>

          <p className="text-xl lg:text-2xl text-[#6b7280] mb-8 leading-relaxed animate-fade-in-up animation-delay-200">
            Still have questions? We’re here to help.
          </p>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Message Display */}
          {message && (
            <div className={`p-4 rounded-xl border-2 animate-fade-in ${
              messageType === 'success'
                ? 'bg-green-50 border-green-200 text-green-600'
                : 'bg-red-50 border-red-200 text-red-600'
            }`}>
              <div className="flex items-center gap-2">
                {messageType === 'success' ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span className="font-medium">{message}</span>
              </div>
            </div>
          )}

          {/* Name Input */}
          <div className="relative group">
            <label htmlFor="name" className="block text-sm font-medium text-[#374151] mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Your Full Name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-4 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-[#1f2937] placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent transition-all duration-300 group-hover:border-[#0ea5e9]"
                required
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                <User className="w-5 h-5 text-[#9ca3af]" />
              </div>
            </div>
          </div>

          {/* Company Input */}
          <div className="relative group">
            <label htmlFor="company" className="block text-sm font-medium text-[#374151] mb-2">
              Company <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                id="company"
                name="company"
                placeholder="Your Company Name"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-4 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-[#1f2937] placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent transition-all duration-300 group-hover:border-[#0ea5e9]"
                required
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                <Building className="w-5 h-5 text-[#9ca3af]" />
              </div>
            </div>
          </div>

          {/* Email Input */}
          <div className="relative group">
            <label htmlFor="email" className="block text-sm font-medium text-[#374151] mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Your Email Address"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-4 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-[#1f2937] placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent transition-all duration-300 group-hover:border-[#0ea5e9]"
                required
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                <Mail className="w-5 h-5 text-[#9ca3af]" />
              </div>
            </div>
          </div>

          {/* Phone Input */}
          <div className="relative group">
            <label htmlFor="phone" className="block text-sm font-medium text-[#374151] mb-2">
              Phone <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder="Your Phone Number"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-4 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-[#1f2937] placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent transition-all duration-300 group-hover:border-[#0ea5e9]"
                required
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                <Phone className="w-5 h-5 text-[#9ca3af]" />
              </div>
            </div>
          </div>

          {/* Products/Services Input */}
          <div className="relative group">
            <label htmlFor="products" className="block text-sm font-medium text-[#374151] mb-2">
              Specific Products, Services or Solutions (Optional)
            </label>
            <div className="relative">
              <input
                type="text"
                id="products"
                name="products"
                placeholder="e.g., Cybersecurity consulting, Cloud security"
                value={formData.products}
                onChange={handleChange}
                className="w-full px-4 py-4 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-[#1f2937] placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent transition-all duration-300 group-hover:border-[#0ea5e9]"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                <Package className="w-5 h-5 text-[#9ca3af]" />
              </div>
            </div>
          </div>

          {/* Consent Checkbox */}
          <div className="flex items-start space-x-3 mt-4">
            <input
              type="checkbox"
              id="consent"
              name="consent"
              checked={formData.consent}
              onChange={handleChange}
              className="form-checkbox h-5 w-5 text-[#0ea5e9] bg-[#f8fafc] border-[#e2e8f0] rounded focus:ring-[#0ea5e9] cursor-pointer"
            />
            <label htmlFor="consent" className="text-[#6b7280] text-sm leading-relaxed">
              I consent to receive promotional communications (which may include
              phone, email, and social) from Glocybs. I understand I may
              proactively opt out of communications with Glocybs at anytime.
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full px-8 py-4 font-bold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg focus:outline-none focus:ring-2 focus:ring-opacity-75 ${
              isSubmitting
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-[#0ea5e9] to-[#3b82f6] text-white hover:from-[#0284c7] hover:to-[#2563eb] focus:ring-[#0ea5e9]'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                SENDING MESSAGE...
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <Mail className="w-5 h-5" /> {/* Lucide Mail icon for submit */}
                SUBMIT MESSAGE
              </div>
            )}
          </button>

          <p className="text-[#9ca3af] text-sm text-center mt-4">
            By clicking submit you agree to the Glocybs{' '}
            <a href="#terms" className="text-[#0ea5e9] hover:underline">Terms and Conditions</a> &amp;{' '}
            <a href="#privacy" className="text-[#0ea5e9] hover:underline">Privacy Policy</a>.
          </p>
        </form>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        /* Import Inter font from Google Fonts */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        .font-inter {
          font-family: 'Inter', sans-serif;
        }

        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes fade-in-up {
          from { 
            opacity: 0; 
            transform: translateY(40px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        
        @keyframes pulse-glow-blue { /* Specific for blue theme */
          0%, 100% { 
            box-shadow: 0 0 20px rgba(14, 165, 233, 0.3),
                        0 0 40px rgba(14, 165, 233, 0.1); 
          }
          50% { 
            box-shadow: 0 0 40px rgba(14, 165, 233, 0.5),
                        0 0 80px rgba(14, 165, 233, 0.2); 
          }
        }
        
        @keyframes rotate-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes gradient-text {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes grid-move {
          0% { background-position: 0 0; }
          100% { background-position: 50px 50px; }
        }

        @keyframes scan-glow-light { /* New glow for light background */
          0% {
            box-shadow: 
              0 0 10px rgba(14, 165, 233, 0.2), 
              0 0 20px rgba(14, 165, 233, 0.1);
          }
          50% {
            box-shadow: 
              0 0 20px rgba(14, 165, 233, 0.4), 
              0 0 40px rgba(14, 165, 233, 0.2);
          }
          100% {
            box-shadow: 
              0 0 10px rgba(14, 165, 233, 0.2), 
              0 0 20px rgba(14, 165, 233, 0.1);
          }
        }

        @keyframes digital-rain-light { /* New digital rain for light background */
          0% { transform: translateY(-100%); opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { transform: translateY(100vh); opacity: 0; }
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-pulse-glow-blue {
          animation: pulse-glow-blue 2s ease-in-out infinite;
        }
        
        .animate-rotate-slow {
          animation: rotate-slow 20s linear infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 30s linear infinite;
        }
        
        .animate-gradient-text {
          background-size: 200% 200%;
          animation: gradient-text 3s ease-in-out infinite;
        }
        
        .animate-grid-move {
          animation: grid-move 20s linear infinite;
        }
        
        .animation-delay-200 {
          animation-delay: 200ms;
        }
        
        .animation-delay-400 {
          animation-delay: 400ms;
        }
        
        .animation-delay-2000 {
          animation-delay: 2000ms;
        }
        
        .animation-delay-4000 {
          animation-delay: 4000ms;
        }
        
        /* Floating Shapes - Light theme versions */
        .floating-shape-light {
          position: absolute;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(59, 130, 246, 0.1));
          animation: float 6s ease-in-out infinite;
        }

        .shape-1-light {
          width: 100px;
          height: 100px;
          top: 10%;
          left: 10%;
          animation-delay: 0s;
        }

        .shape-2-light {
          width: 150px;
          height: 150px;
          top: 60%;
          right: 10%;
          animation: float-reverse 8s ease-in-out infinite;
          animation-delay: 2s;
        }

        .shape-3-light {
          width: 80px;
          height: 80px;
          top: 30%;
          right: 30%;
          animation-delay: 4s;
        }

        .shape-4-light {
          width: 120px;
          height: 120px;
          bottom: 20%;
          left: 20%;
          animation: float-reverse 7s ease-in-out infinite;
          animation-delay: 1s;
        }

        /* Grid Pattern - Light theme version */
        .bg-grid-pattern-light {
          background-image: 
            linear-gradient(rgba(14, 165, 233, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14, 165, 233, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
        }
        
        /* Scanning Lines - Light theme versions */
        .scan-line-1-light {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, transparent, rgba(14, 165, 233, 0.5), transparent); /* Lighter blue */
          animation: scan-horizontal 8s linear infinite;
        }
        
        .scan-line-2-light {
          position: absolute;
          top: 0;
          left: 0;
          width: 2px;
          height: 100%;
          background: linear-gradient(180deg, transparent, rgba(59, 130, 246, 0.5), transparent); /* Lighter blue */
          animation: scan-vertical 12s linear infinite;
        }
        
        @keyframes scan-horizontal {
          0% { transform: translateY(0); }
          50% { transform: translateY(100vh); }
          50.01% { transform: translateY(-100vh); }
          100% { transform: translateY(0); }
        }

        @keyframes scan-vertical {
          0% { transform: translateX(0); }
          50% { transform: translateX(100vw); }
          50.01% { transform: translateX(-100vw); }
          100% { transform: translateX(0); }
        }

        /* Digital Rain Styles - Light theme version */
        .digital-rain-container {
          pointer-events: none;
          z-index: 0;
        }
        .digital-rain-line-light { /* Changed class name */
          position: absolute;
          width: 1px;
          height: 50px;
          background: linear-gradient(to bottom, transparent, rgba(14, 165, 233, 0.5), transparent); /* Lighter blue */
          animation: digital-rain-light linear infinite; /* Changed animation name */
          opacity: 0;
        }
      `}</style>
    </section>
  );
}

export default ContactUs;
